import { useState, useMemo, useEffect, useRef } from 'react';

import Alert from '../../../alert';

import { stripParentheses } from '../../../../../utils';

import { decisionTypes } from './data';
import {
  classificationDecisionTypeCards,
  classificationRecommendedByLevel,
  classificationModalCopy,
  classificationTheme,
} from './classificationModalData';
import { REALCO_LEVEL_PREFIX } from '../../../../Classification/realcoData';

// Reuse the BE modal stylesheet — class names + animations are
// identical; classification's bg/border/divider/text differ only in
// values, exposed as CSS variables overridden by `classificationTheme`
// on the modal root.
import './balancedEquationLevelModal.css';

const STEP_DECISION = 'decision';
const STEP_SCENARIO = 'scenario';

// Stable empty-array reference — see BE modal for explanation.
const EMPTY_PRECHECKED = [];

// Step 2 group accounts under their parent financial-statement element
// rather than a single "Transactions" list. Each item's `column` is set
// to one of these by mapAccountQuestionToFilterItem in the parent.
const ELEMENT_COLUMNS = ['Assets', 'Liabilities', 'Equity', 'Revenues', 'Expenses'];

// 2-step add-level flow for Account Classification. Mirrors the
// Balanced Equation modal in structure and behavior; differs only in
// card copy, card color theme, and the source decisionTypes lookup.
//
// 'class-elements' (the auto-created Level 1 decision type) is not
// listed in classificationDecisionTypeCards, so it never appears as
// a selectable option in this modal — it doesn't have to.
// Elements-preset card key. Reserved for the auto-create "sort the 5
// elements into Balance Sheet vs Income Statement" tutorial-style Level
// 1. Only surfaced when showElementsPresetCard is true (Review Session
// create-first-level flow); clicking it bypasses step 2 entirely and
// calls onElementsPresetSelected so the parent can fire a one-shot
// create of the class-elements level from the template.
const ELEMENTS_PRESET_CARD = {
  key: 'class-elements',
  title: 'The Elements',
  description: [
    { text: 'Students sort the 5 financial-statement elements (Assets, Liabilities, Equity, Revenues, Expenses) into ' },
    { text: 'Balance Sheet', bold: true },
    { text: ' vs. ' },
    { text: 'Income Statement', bold: true },
    { text: '. Auto-created with the standard 5 elements and 2 drop zones.' },
  ],
};

export default function ClassificationLevelModal({
  scenarioLevelNumber,
  filterItems,
  isButtonLoading,
  onCloseButtonClick,
  onSaveButtonClick,
  decisionType = '',
  editingScenarioLevelPreCheckedItems = EMPTY_PRECHECKED,
  businessType,
  showElementsPresetCard = false,
  onElementsPresetSelected,
}) {
  const gameType = 'Account Classification';
  const labelByKey = decisionTypes[gameType] || {};
  const isEditMode = !!decisionType;

  // Hide the "Deferral & Contra Accounts" card while the shared
  // deferral-contra-template scenario level is unpopulated on the
  // backend — selecting it would create an empty level. Remove this
  // filter once the template has content.
  //
  // "Real Companies" is only relevant for corporate-entity courses
  // (real publicly-traded companies file under corporate accounting),
  // so hide it for any other businesstype.
  const cards = useMemo(() => {
    const base = classificationDecisionTypeCards.filter(c => {
      if (c.key === 'class-tricky') return false;
      if (c.key === 'real-public-co' && businessType !== 'Corporation') return false;
      return true;
    });
    // Elements preset renders first so it reads as the recommended
    // starting point when both are available.
    return showElementsPresetCard ? [ELEMENTS_PRESET_CARD, ...base] : base;
  }, [businessType, showElementsPresetCard]);

  const [step, setStep] = useState(isEditMode ? STEP_SCENARIO : STEP_DECISION);
  const [leavingStep, setLeavingStep] = useState(null);
  const [selectedDecisionTypeKey, setSelectedDecisionTypeKey] = useState(
    isEditMode ? decisionType : null
  );
  const [selectedScenarioName, setSelectedScenarioName] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [pendingScenarioName, setPendingScenarioName] = useState(null);

  const editInitPendingRef = useRef(isEditMode);

  const recommendedKey = classificationRecommendedByLevel[scenarioLevelNumber] || null;

  // Real Companies levels are stored under company-specific aliases
  // (`real-public-co-Apple`, `real-public-co-Microsoft`, ...) — see
  // edit.js:667 and buildRealcoLevelName. Reduce those back to the
  // canonical `real-public-co` card key so edit mode resolves the
  // pinned card + label bucket; without this, `selectedCard` is null
  // and step 2 renders as an empty modal.
  const resolvedCardKey = selectedDecisionTypeKey?.startsWith(REALCO_LEVEL_PREFIX)
    ? 'real-public-co'
    : selectedDecisionTypeKey;

  const selectedCard = useMemo(
    () => classificationDecisionTypeCards.find((c) => c.key === resolvedCardKey) || null,
    [resolvedCardKey]
  );

  const decisionTypeLabel = resolvedCardKey ? labelByKey[resolvedCardKey] : null;
  const scenarioBucket = decisionTypeLabel ? filterItems?.[decisionTypeLabel] : null;
  const scenarioNames = useMemo(
    () => (scenarioBucket ? Object.keys(scenarioBucket) : []),
    [scenarioBucket]
  );

  const initialEditScenarioName = useMemo(() => {
    if (!isEditMode || !scenarioBucket || !editingScenarioLevelPreCheckedItems.length) {
      return null;
    }
    const precheckedTexts = new Set(
      editingScenarioLevelPreCheckedItems.map((p) => p.text)
    );
    let best = null;
    let bestScore = 0;
    Object.entries(scenarioBucket).forEach(([name, items]) => {
      const score = items.filter((it) => precheckedTexts.has(it.text)).length;
      if (score > bestScore) {
        best = name;
        bestScore = score;
      }
    });
    return best;
  }, [isEditMode, scenarioBucket, editingScenarioLevelPreCheckedItems]);

  useEffect(() => {
    if (step === STEP_SCENARIO && !selectedScenarioName && scenarioNames.length) {
      setSelectedScenarioName(initialEditScenarioName || scenarioNames[0]);
    }
    if (step === STEP_DECISION) {
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
    }
  }, [step, scenarioNames, selectedScenarioName, initialEditScenarioName]);

  const visibleTransactions = useMemo(() => {
    if (!scenarioBucket || !selectedScenarioName) return [];
    return scenarioBucket[selectedScenarioName] || [];
  }, [scenarioBucket, selectedScenarioName]);

  // visibleTransactions intentionally omitted from deps — see BE
  // modal for the explanation; including it clobbers user unchecks
  // on every parent re-render.
  useEffect(() => {
    if (step !== STEP_SCENARIO || !selectedScenarioName) return;

    if (editInitPendingRef.current && editingScenarioLevelPreCheckedItems.length) {
      const precheckedTexts = new Set(
        editingScenarioLevelPreCheckedItems.map((p) => p.text)
      );
      setSelectedTransactions(
        visibleTransactions.filter((t) => precheckedTexts.has(t.text))
      );
      editInitPendingRef.current = false;
      return;
    }

    setSelectedTransactions(visibleTransactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedScenarioName, editingScenarioLevelPreCheckedItems]);

  const handleCardClick = (key) => {
    if (step !== STEP_DECISION) return;
    // Elements preset is a one-click action — hand control back to the
    // parent to fire the create + close the modal.
    if (key === ELEMENTS_PRESET_CARD.key && showElementsPresetCard) {
      onElementsPresetSelected?.();
      return;
    }
    setSelectedDecisionTypeKey(key);
    setStep(STEP_SCENARIO);
  };

  const handleChangeDecisionType = () => {
    setLeavingStep(STEP_SCENARIO);
    setTimeout(() => {
      setStep(STEP_DECISION);
      setSelectedDecisionTypeKey(null);
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
      setLeavingStep(null);
    }, 220);
  };

  const handleScenarioRadioClick = (name) => {
    if (name === selectedScenarioName) return;

    const hasCustomized =
      selectedTransactions.length !== visibleTransactions.length ||
      !visibleTransactions.every((t) =>
        selectedTransactions.some((s) => s.id === t.id)
      );

    if (hasCustomized && selectedTransactions.length > 0) {
      setPendingScenarioName(name);
      return;
    }
    setSelectedScenarioName(name);
  };

  const handleScenarioSwitchConfirm = () => {
    setSelectedScenarioName(pendingScenarioName);
    setSelectedTransactions([]);
    setPendingScenarioName(null);
  };

  const handleScenarioSwitchCancel = () => {
    setPendingScenarioName(null);
  };

  const handleTransactionToggle = (item, isChecked) => {
    setSelectedTransactions((prev) => {
      if (isChecked) {
        if (prev.some((t) => t.id === item.id)) return prev;
        return [...prev, item];
      }
      return prev.filter((t) => t.id !== item.id);
    });
  };

  // Bulk select / deselect toggle for the accounts grid. See
  // balancedEquationLevelModal.js for the full rationale.
  const areAllVisibleSelected =
    visibleTransactions.length > 0
    && visibleTransactions.every((t) => selectedTransactions.some((s) => s.id === t.id));
  const areNoneVisibleSelected =
    visibleTransactions.length > 0
    && !visibleTransactions.some((t) => selectedTransactions.some((s) => s.id === t.id));

  // Sticky label: the toggle only flips when the user clicks it OR
  // when the visible selection hits a boundary (all → 'deselect',
  // none → 'select'). A single incidental uncheck no longer swaps
  // the label out from under the user.
  const [selectAllMode, setSelectAllMode] = useState('select');
  useEffect(() => {
    if (areAllVisibleSelected) setSelectAllMode('deselect');
    else if (areNoneVisibleSelected) setSelectAllMode('select');
  }, [areAllVisibleSelected, areNoneVisibleSelected]);

  const handleToggleAllTransactions = () => {
    if (selectAllMode === 'deselect') {
      const visibleIds = new Set(visibleTransactions.map((t) => t.id));
      setSelectedTransactions((prev) => prev.filter((t) => !visibleIds.has(t.id)));
    } else {
      setSelectedTransactions((prev) => {
        const merged = [...prev];
        visibleTransactions.forEach((t) => {
          if (!merged.some((s) => s.id === t.id)) merged.push(t);
        });
        return merged;
      });
    }
  };

  const handleSaveClick = () => {
    if (!selectedTransactions.length) return;
    onSaveButtonClick(
      selectedTransactions,
      selectedScenarioName,
      decisionTypeLabel,
      onCloseButtonClick,
      gameType,
      selectedDecisionTypeKey
    );
  };

  const renderCard = (card, opts = {}) => {
    const { pinned = false, allowClick = true, footer = null } = opts;
    const isRecommended = card.key === recommendedKey;
    const isSelected = card.key === selectedDecisionTypeKey;
    return (
      <div
        key={card.key}
        className={`be-level-card${pinned ? ' be-level-modal-pinned-card' : ''}`}
        data-selected={isSelected ? 'true' : 'false'}
        onClick={allowClick && !pinned ? () => handleCardClick(card.key) : undefined}
        role={allowClick && !pinned ? 'button' : undefined}
        tabIndex={allowClick && !pinned ? 0 : undefined}
      >
        <h3 className="be-level-card-title">{card.title}</h3>
        <hr className="be-level-card-divider" />
        <p className="be-level-card-description">
          {card.description.map((seg, i) =>
            seg.bold ? <b key={i}>{seg.text}</b> : <span key={i}>{seg.text}</span>
          )}
        </p>
        {isRecommended && !pinned && (
          <div className="be-level-card-recommended">
            {classificationModalCopy.recommendedBadge.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
        {footer}
      </div>
    );
  };

  return (
    <div className="be-level-modal-overlay">
      <div
        className="be-level-modal"
        data-step={step}
        data-leaving={leavingStep || undefined}
        data-game="classification"
        style={classificationTheme}
      >
        <button
          className="be-level-modal-close"
          onClick={onCloseButtonClick}
          aria-label="Close"
        >
          ×
        </button>

        <div className="be-level-modal-header">
          <h2 className="be-level-modal-title">
            {step === STEP_DECISION
              ? classificationModalCopy.title(scenarioLevelNumber)
              : classificationModalCopy.step2Title(scenarioLevelNumber)}
          </h2>
          <p className="be-level-modal-subtitle">
            {step === STEP_DECISION
              ? classificationModalCopy.subtitle
              : classificationModalCopy.step2Subtitle}
          </p>
        </div>

        {step === STEP_DECISION && (
          <div className="be-level-modal-cards">
            {cards.map((card) => renderCard(card))}
          </div>
        )}

        {step === STEP_SCENARIO && selectedCard && (
          <div className="be-level-modal-step2">
            {renderCard(selectedCard, {
              pinned: true,
              allowClick: false,
              footer: isEditMode ? null : (
                <button
                  type="button"
                  className="be-level-modal-back-button be-level-modal-pinned-back-button"
                  onClick={handleChangeDecisionType}
                >
                  {classificationModalCopy.changeDecisionTypeButton}
                </button>
              ),
            })}

            <div className="be-level-modal-step2-columns">
              <div>
                <h3 className="be-level-modal-column-header">
                  {classificationModalCopy.scenariosColumnHeader}
                </h3>
                <div className="be-level-modal-scenarios">
                  {scenarioNames.map((name) => {
                    const active = name === selectedScenarioName;
                    return (
                      <label
                        key={name}
                        className={`be-level-modal-scenario-option${active ? ' be-level-modal-scenario-option--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="classification-business-scenario"
                          checked={active}
                          onChange={() => handleScenarioRadioClick(name)}
                        />
                        <span>{stripParentheses(name)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="be-level-modal-column-header-row">
                  <h3 className="be-level-modal-column-header">
                    {classificationModalCopy.accountsColumnHeader}
                  </h3>
                  {visibleTransactions.length > 0 && (
                    <button
                      type="button"
                      className="be-level-modal-toggle-all"
                      onClick={handleToggleAllTransactions}
                    >
                      {selectAllMode === 'deselect' ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>
                <div className="be-level-modal-elements-grid">
                {ELEMENT_COLUMNS.map((columnTitle) => {
                  const columnItems = visibleTransactions.filter((t) => t.column === columnTitle);
                  return (
                    <div key={columnTitle} className="be-level-modal-element-column">
                      <h4 className="be-level-modal-element-header">{columnTitle}</h4>
                      <div className="be-level-modal-element-list">
                        {columnItems.map((item) => {
                          const checked = selectedTransactions.some((t) => t.id === item.id);
                          return (
                            <label key={item.id} className="be-level-modal-transaction-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => handleTransactionToggle(item, e.target.checked)}
                              />
                              <span>{item.text}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="be-level-modal-footer">
          <span />
          {step === STEP_SCENARIO && (
            <button
              type="button"
              className={`button-blue${isButtonLoading ? ' loading' : ''}`}
              disabled={!selectedTransactions.length || isButtonLoading}
              onClick={handleSaveClick}
            >
              {classificationModalCopy.saveButton}
            </button>
          )}
        </div>
      </div>

      {pendingScenarioName && (
        <Alert
          type="warning"
          title={classificationModalCopy.switchScenarioWarningTitle}
          text={classificationModalCopy.switchScenarioWarningText}
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirmButtonClick={handleScenarioSwitchConfirm}
          onCancelButtonClick={handleScenarioSwitchCancel}
        />
      )}
    </div>
  );
}
